/* global bootstrap */

/**
 * Tunnel Save Confirmation Handler
 * Intercepts form submission to show a confirmation modal with changes summary
 * Always shows modal for both CREATE and EDIT so users can review before submitting
 */

document.addEventListener('DOMContentLoaded', function() {
  const form = document.querySelector('form[method="POST"]');
  if (!form) return;

  // Only run on tunnel pages (when modal exists)
  const modal = document.getElementById('saveConfirmationModal');
  if (!modal) return;

  const isTunnelPage = window.location.pathname.includes('/tunnels/');
  if (!isTunnelPage) return;

  // Detect if we're creating or editing based on button text
  const submitButton = form.querySelector('button[type="submit"]');
  const isCreateMode = submitButton && submitButton.textContent.includes('Create Tunnel');

  let originalFormData = null;
  let isConfirmed = false;

  // Capture initial form state
  function captureFormState() {
    const data = {};

    // Handle all form inputs manually to ensure checkboxes are captured correctly
    const inputs = form.querySelectorAll('input, select, textarea');

    inputs.forEach(input => {
      if (input.type === 'checkbox') {
        if (input.name === 'routing.network_ids' && input.checked) {
          // Multiple network checkboxes
          if (!data['routing.network_ids']) data['routing.network_ids'] = [];
          data['routing.network_ids'].push(input.value);
        } else if (input.checked) {
          // Other checkboxes
          data[input.name] = input.value;
        }
      } else if (input.type === 'radio') {
        if (input.checked) {
          data[input.name] = input.value;
        }
      } else if (input.tagName === 'SELECT') {
        data[input.name] = input.value;
      } else if (input.type !== 'submit' && input.type !== 'button') {
        data[input.name] = input.value;
      }
    });

    return data;
  }

  // Wait a bit for page to fully load before capturing initial state
  setTimeout(() => {
    originalFormData = captureFormState();
  }, 500);

  // Intercept form submission - ALWAYS show modal
  form.addEventListener('submit', function(e) {
    // If already confirmed, allow submission
    if (isConfirmed) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const currentFormData = captureFormState();

    if (isCreateMode) {
      // Show what will be created
      showCreateConfirmationModal(currentFormData);
    } else {
      // Show what will be changed
      const changes = detectChanges(originalFormData, currentFormData);
      showConfirmationModal(changes, currentFormData);
    }
  });

  function detectChanges(original, current) {
    const changes = [];

    // Check name change
    if (original?.name !== current?.name) {
      changes.push({
        type: 'name',
        label: 'Tunnel Name',
        from: original?.name || '(not set)',
        to: current?.name
      });
    }

    // Check routing type change
    if (original?.['routing.type'] !== current?.['routing.type']) {
      changes.push({
        type: 'routing_type',
        label: 'Routing Protocol',
        from: formatRoutingType(original?.['routing.type']),
        to: formatRoutingType(current?.['routing.type'])
      });
    }

    // Check network changes (for static/bgp routing)
    const originalNetworks = Array.isArray(original?.['routing.network_ids'])
      ? original['routing.network_ids']
      : (original?.['routing.network_ids[]'] || []);
    const currentNetworks = Array.isArray(current?.['routing.network_ids'])
      ? current['routing.network_ids']
      : (current?.['routing.network_ids[]'] || []);

    const addedNetworks = currentNetworks.filter(n => !originalNetworks.includes(n));
    const removedNetworks = originalNetworks.filter(n => !currentNetworks.includes(n));

    if (addedNetworks.length > 0 || removedNetworks.length > 0) {
      changes.push({
        type: 'networks',
        added: addedNetworks,
        removed: removedNetworks
      });
    }

    // Check ASN change (for BGP routing)
    if (original?.['routing.asn_id'] !== current?.['routing.asn_id']) {
      changes.push({
        type: 'asn',
        label: 'ASN',
        from: original?.['routing.asn_id'] || '(not set)',
        to: current?.['routing.asn_id'] || '(not set)'
      });
    }

    // Check default route change
    const originalDefaultRoute = original?.['routing.send_default_route'] === 'true';
    const currentDefaultRoute = current?.['routing.send_default_route'] === 'true';

    if (originalDefaultRoute !== currentDefaultRoute) {
      changes.push({
        type: 'default_route',
        label: 'Send Default Route',
        from: originalDefaultRoute ? 'Enabled' : 'Disabled',
        to: currentDefaultRoute ? 'Enabled' : 'Disabled'
      });
    }

    // Check public key change (WireGuard)
    if (original?.public_key !== current?.public_key) {
      const originalKey = original?.public_key || '(not set)';
      const currentKey = current?.public_key || '(not set)';

      // Only show change if it's actually different
      if (originalKey !== currentKey) {
        changes.push({
          type: 'public_key',
          label: 'Public Key',
          from: originalKey.length > 20 ? originalKey.substring(0, 20) + '...' : originalKey,
          to: currentKey.length > 20 ? currentKey.substring(0, 20) + '...' : currentKey
        });
      }
    }

    // Check preshared key change (WireGuard)
    if (original?.preshared_key !== current?.preshared_key) {
      const originalKey = original?.preshared_key || '(not set)';
      const currentKey = current?.preshared_key || '(not set)';

      // Only show change if it's actually different
      if (originalKey !== currentKey) {
        const keyChanged = originalKey !== '(not set)' && currentKey !== '(not set)';
        changes.push({
          type: 'preshared_key',
          label: 'Preshared Key',
          from: keyChanged ? 'Set (hidden)' : originalKey,
          to: keyChanged ? 'Regenerated (hidden)' : currentKey
        });
      }
    }

    return changes;
  }

  function formatRoutingType(type) {
    if (!type || type === '') return 'None';
    return type.toUpperCase();
  }

  function showConfirmationModal(changes, formData) {
    const modal = new bootstrap.Modal(document.getElementById('saveConfirmationModal'));
    const summaryContainer = document.getElementById('changesSummary');
    const conflictWarnings = document.getElementById('conflictWarnings');
    const conflictList = document.getElementById('conflictList');

    // Build changes summary HTML
    let summaryHTML = '<ul class="list-unstyled mb-0">';

    changes.forEach(change => {
      if (change.type === 'networks') {
        if (change.added.length > 0) {
          summaryHTML += '<li class="mb-2"><i class="fas fa-plus-circle text-success me-2"></i>';
          summaryHTML += `<strong>Adding ${change.added.length} network(s)</strong>`;
          summaryHTML += '<ul class="mt-1">';
          change.added.forEach(netId => {
            const netCheckbox = document.querySelector(`input[name="routing.network_ids"][value="${netId}"]`);
            if (netCheckbox) {
              const networkName = netCheckbox.getAttribute('data-network-name') ||
                                  netCheckbox.parentElement.querySelector('label').textContent.trim();
              summaryHTML += `<li>${networkName}</li>`;
            }
          });
          summaryHTML += '</ul></li>';
        }

        if (change.removed.length > 0) {
          summaryHTML += '<li class="mb-2"><i class="fas fa-minus-circle text-danger me-2"></i>';
          summaryHTML += `<strong>Removing ${change.removed.length} network(s)</strong>`;
          summaryHTML += '<ul class="mt-1">';
          change.removed.forEach(netId => {
            const netCheckbox = document.querySelector(`input[name="routing.network_ids"][value="${netId}"]`);
            if (netCheckbox) {
              const networkName = netCheckbox.getAttribute('data-network-name') ||
                                  netCheckbox.parentElement.querySelector('label').textContent.trim();
              summaryHTML += `<li>${networkName}</li>`;
            }
          });
          summaryHTML += '</ul></li>';
        }
      } else {
        summaryHTML += `<li class="mb-2"><i class="fas fa-edit text-primary me-2"></i>`;
        summaryHTML += `<strong>${change.label}:</strong> ${change.from} → ${change.to}</li>`;
      }
    });

    summaryHTML += '</ul>';
    summaryContainer.innerHTML = summaryHTML;

    // Check for conflicts (networks already used in other tunnels)
    const conflicts = checkNetworkConflicts(formData);
    if (conflicts.length > 0) {
      conflictWarnings.classList.remove('d-none');
      let conflictHTML = '';
      conflicts.forEach(conflict => {
        conflictHTML += `<li>${conflict}</li>`;
      });
      conflictList.innerHTML = conflictHTML;
    } else {
      conflictWarnings.classList.add('d-none');
    }

    // Show modal
    modal.show();
  }

  function showCreateConfirmationModal(formData) {
    const modal = new bootstrap.Modal(document.getElementById('saveConfirmationModal'));
    const summaryContainer = document.getElementById('changesSummary');
    const conflictWarnings = document.getElementById('conflictWarnings');
    const conflictList = document.getElementById('conflictList');

    // Build summary of what will be created
    let summaryHTML = '<ul class="list-unstyled mb-0">';

    // Tunnel name
    if (formData.name) {
      summaryHTML += `<li class="mb-2"><i class="fas fa-plus-circle text-success me-2"></i>`;
      summaryHTML += `<strong>Tunnel Name:</strong> ${formData.name}</li>`;
    }

    // Routing type
    const routingType = formData['routing.type'];
    if (routingType) {
      summaryHTML += `<li class="mb-2"><i class="fas fa-route text-primary me-2"></i>`;
      summaryHTML += `<strong>Routing Protocol:</strong> ${formatRoutingType(routingType)}</li>`;
    }

    // Networks
    const networks = Array.isArray(formData['routing.network_ids'])
      ? formData['routing.network_ids']
      : (formData['routing.network_ids[]'] || []);
    if (networks.length > 0) {
      summaryHTML += '<li class="mb-2"><i class="fas fa-network-wired text-success me-2"></i>';
      summaryHTML += `<strong>Attaching ${networks.length} network(s)</strong>`;
      summaryHTML += '<ul class="mt-1">';
      networks.forEach(netId => {
        const netCheckbox = document.querySelector(`input[name="routing.network_ids"][value="${netId}"]`);
        if (netCheckbox) {
          const networkName = netCheckbox.getAttribute('data-network-name') ||
                              netCheckbox.parentElement.querySelector('label').textContent.trim();
          summaryHTML += `<li>${networkName}</li>`;
        }
      });
      summaryHTML += '</ul></li>';
    }

    // ASN (for BGP)
    if (routingType === 'bgp' && formData['routing.asn_id']) {
      const asnOption = document.querySelector(`#asn_id option[value="${formData['routing.asn_id']}"]`);
      if (asnOption) {
        summaryHTML += `<li class="mb-2"><i class="fas fa-hashtag text-primary me-2"></i>`;
        summaryHTML += `<strong>ASN:</strong> ${asnOption.textContent}</li>`;
      }
    }

    // Default route
    if (formData['routing.send_default_route'] === 'true') {
      summaryHTML += `<li class="mb-2"><i class="fas fa-globe text-info me-2"></i>`;
      summaryHTML += `<strong>Send Default Route:</strong> Enabled</li>`;
    }

    // Public key
    if (formData.public_key) {
      const truncatedKey = formData.public_key.length > 20
        ? formData.public_key.substring(0, 20) + '...'
        : formData.public_key;
      summaryHTML += `<li class="mb-2"><i class="fas fa-key text-primary me-2"></i>`;
      summaryHTML += `<strong>Public Key:</strong> ${truncatedKey}</li>`;
    }

    // Preshared key
    if (formData.preshared_key) {
      summaryHTML += `<li class="mb-2"><i class="fas fa-shield-alt text-warning me-2"></i>`;
      summaryHTML += `<strong>Preshared Key:</strong> Set (hidden)</li>`;
    }

    // Send tunnel details by email
    const sendEmail = formData.notify ? 'Yes' : 'No';
    summaryHTML += `<li class="mb-2"><i class="fas fa-envelope text-${formData.notify ? 'info' : 'muted'} me-2"></i>`;
    summaryHTML += `<strong>Send tunnel details by email:</strong> ${sendEmail}</li>`;

    summaryHTML += '</ul>';
    summaryContainer.innerHTML = summaryHTML;

    // Check for conflicts
    const conflicts = checkNetworkConflicts(formData);
    if (conflicts.length > 0) {
      conflictWarnings.classList.remove('d-none');
      let conflictHTML = '';
      conflicts.forEach(conflict => {
        conflictHTML += `<li>${conflict}</li>`;
      });
      conflictList.innerHTML = conflictHTML;
    } else {
      conflictWarnings.classList.add('d-none');
    }

    // Show modal
    modal.show();
  }

  function checkNetworkConflicts(formData) {
    const conflicts = [];
    const selectedNetworks = Array.isArray(formData['routing.network_ids'])
      ? formData['routing.network_ids']
      : (formData['routing.network_ids[]'] || []);

    selectedNetworks.forEach(netId => {
      const netCheckbox = document.querySelector(`input[name="routing.network_ids"][value="${netId}"]`);
      if (netCheckbox) {
        const label = netCheckbox.parentElement.querySelector('label');
        const warningBadge = label.querySelector('.badge.bg-warning');
        if (warningBadge) {
          // Extract network name and conflict info
          const networkName = netCheckbox.getAttribute('data-network-name') ||
                             label.textContent.split('⚠️')[0].trim();
          const conflictText = warningBadge.textContent.replace('⚠️', '').trim();
          conflicts.push(`${networkName}: ${conflictText}`);
        }
      }
    });

    return conflicts;
  }

  // Handle confirm button click
  document.getElementById('confirmSaveButton').addEventListener('click', function() {
    isConfirmed = true;
    bootstrap.Modal.getInstance(document.getElementById('saveConfirmationModal')).hide();
    form.submit();
  });
});
